import React, { useState, useEffect, useRef } from 'react';
import { Modal, Box, RadioGroup, FormControlLabel, Radio, Alert, AlertTitle, Collapse, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import CustomDeleteConfirmationModal from '../../CustomDeleteConfirmationModal/CustomDeleteConfirmationModal';
import { Tooltip, IconButton, Stack, Button } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
// For PDF generation
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

// For toast messages (optional)
import { toast } from "react-toastify";

// For i18n (optional)
import { useTranslation } from 'react-i18next';
import NoDocumentsPdf from "../../../assests/pdf/NoDocument.pdf";

// Services
import TaxAuthorityService from '../../../Services/taxAuthority.service';
import ClientService from '../../../Services/client.service';
import { getVatValue } from '../../../Utils/GetVatValue';
import Constants from '../../../assests/Constants/constants';
import PuffLoader from "react-spinners/PuffLoader";
import dayjs, { Dayjs } from 'dayjs';
import Switch from '@mui/material/Switch';


import ReportsService from '../../../Services/reports.service';
import vatImage from '../../../assests/images/VATReport.jpg';
import { beautifyNumber, roundVatToInteger } from '../../../Utils/FormatNumber';
import CustomMonthYearIndividualDatePicker from '../CustomDates/CustomMonthYearIndividualDatePicker/CustomMonthYearIndividualDatePicker';
import BiMonthlyPeriodModal from '../../ClientDetails/Modals/BiMonthlyPeriodModal';
import HtmlViewerModal from '../HtmlViewerModal/HtmlViewerModal';
import DocumentService from '../../../Services/documents.service';
import EditModal from '../../EditModal/EditModal';
import { validateDocumentBeforeProcessing } from '../../../Utils/documentValidation';
import EnumsService from '../../../Services/enums.service';
import AssetService from '../../../Services/assets.service';
import ClientDocumentCountService from '../../../Services/ClientDocumentCount.service';
import './VatModal.css'
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import utc from 'dayjs/plugin/utc';
import PCNModal from '../../PCN/PCNModal.jsx';
import { useHamburgerMenu } from '../../../Contexts/HamburgerMenuContext.js';
import { VatModalHeader, AccumulatedVatPanel, VatModalActions, VatModalAlerts } from './VatModalComponents';
import YearlyVatReportModal from '../YearlyVatReport/YearlyVatReportModal';

dayjs.extend(utc);
const MySwal = withReactContent(Swal);

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '92%',
    maxWidth: '1400px',
    height: '92vh',
    borderRadius: '16px',
    background: '#FFF',
    boxShadow: '0 12px 40px rgba(48, 79, 255, 0.12), 0 4px 12px rgba(0,0,0,0.08)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
};

const formatDateToYearMonth = (dateString) => {
    const date = new Date(dateString);
    const year = date.getUTCFullYear();
    const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
    return `${year}/${month}`;
};

const formatMonthYearDate = dateString => {
    const date = new Date(dateString);

    const month = ('0' + (date.getUTCMonth() + 1)).slice(-2);
    const year = date.getUTCFullYear();
    return `${month}/${year}`;
};

/** Check reference sequence across the full calendar year of the given date. */
const getFullYearDateRange = (dateValue) => {
    const year = dayjs(dateValue).year();
    return {
        start: dayjs.utc(new Date(Date.UTC(year, 0, 1))).toDate(),
        end: dayjs.utc(new Date(Date.UTC(year, 11, 31))).toDate(),
    };
};

/** Parse clickable transaction rows from VAT HTML for EditModal next/prev navigation */
const parseVatHtmlNavigationRows = (html) => {
    if (!html) return [];
    try {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        return Array.from(doc.querySelectorAll('tr.tr[data-id][data-type]'))
            .map((tr) => {
                const id = tr.getAttribute('data-id');
                const type = tr.getAttribute('data-type');
                if (!id || type == null || type === '') return null;
                return { id: String(id), type: String(type) };
            })
            .filter(Boolean);
    } catch (err) {
        console.error('Failed to parse VAT HTML rows:', err);
        return [];
    }
};

// Tax Authority payment methods. Only these two are accepted on a VAT payment report;
// 0 exists in the client's bank details but means "not set" as far as reporting goes.
const TA_DIRECT_DEBIT = 1;
const TA_CREDIT_CARD = 2;

export default function VatModal({
    open,
    onClose,
    /** The client/item data object from the parent */
    item,
    client,
    /** The logged in user & token (if needed for API calls) */
    user,
    token,
    startDate,
    endDate,
    isRange,
    isBiMonthly,
    date,
    biStartDate,
    biEndDate,
    showBiMonth,
    setShowBiMonth,
    handleStartDateChangeNew,
    handleEndDateChangeNew,
    handleDateChangeNew,
    handleBiMonthDate,
    handleRangeChange,
    isMainLoading,
    fetchDataOnDateRangeChange,
    fetchDocumentsNotification,
    downloadPCNReport,
    /** Navigation function to view document details */
    viewDocumentDetails,
    /**
     * Mirrors the "OnlyProcessedDocuments" header toggle from ClientDetails.
     * - true  → VAT figures here exclude unprocessed docs (we warn that they exist but are NOT included)
     * - false → VAT figures here include unprocessed docs (we warn that the totals already include them)
     */
    onlyProcessedDocuments = false,
    /** Callback after we finish in the child (to refresh parent's data) */
    onDataUpdate = () => { },
}) {
    const { t, i18n } = useTranslation();
    const direction = i18n.dir();

    // ======= LOCAL STATES =======
    // The entire VAT form data
    const [vatReport, setVatReport] = useState({});

    // Reporting way => 'automatic' | 'manual' | 'unreported'
    const [reportingWay, setReportingWay] = useState('unreported');
    // Optional: close the reporting month after a manual or automatic report is saved
    const [closeReportingMonth, setCloseReportingMonth] = useState(false);
    const closeReportingMonthRef = useRef(false);
    const [reportingMonthsOpen, setReportingMonthsOpen] = useState(true);
    // Number of payments for automatic reporting (1-4)
    const [numberOfPayments, setNumberOfPayments] = useState(1);
    // How the Tax Authority should collect an amount due: 1 - direct debit, 2 - credit card
    const [paymentMethod, setPaymentMethod] = useState(TA_DIRECT_DEBIT);
    // For the “Are you sure?” style confirmation
    const [actionType, setActionType] = useState('');
    const [isConfirmationVisible, setIsConfirmationVisible] = useState(false);
    // Possibly loading state
    const [loading, setLoading] = useState(false);
    const [showYearlyVatReport, setShowYearlyVatReport] = useState(false);
    const [isHtmlModalOpen, setIsHtmlModalOpen] = useState(false);
    const [vatHtml, setVatHtml] = useState('');
    const [vatHtmlTitle, setVatHtmlTitle] = useState('');
    const [vatTypeNumber, setVatTypeNumber] = useState(0);
    /** Rows parsed from VAT HTML for EditModal next/prev (id + entry type) */
    const [vatHtmlNavigationList, setVatHtmlNavigationList] = useState([]);
    const [currentVatEditIndex, setCurrentVatEditIndex] = useState(-1);
    const [accumulatedVat, setAccumulatedVat] = useState(null);
    /** True when this period has VAT control account entries (code 1 / code 2) – show explanation. */
    const [hasVatControlAccountEntries, setHasVatControlAccountEntries] = useState(false);
    /** Net amount from code-1/self-invoice adjustments (for display in explanation). */
    const [code1AdjustmentAmount, setCode1AdjustmentAmount] = useState(0);

    const [isEditSingleEntryOpen, setIsEditSingleEntryOpen] = useState(false);
    // This holds data about token / tax authority flow
    const [taxAuthorityData, setTaxAuthorityData] = useState({
        authUrl: '',
        resumeTaxAuthorityVatOperation: false,
        UpdatedAdd: {}
    });

    //EditModal Vars
    const [editedItem, setEditedItem] = useState({});
    const [oldAccountName, setOldAccountName] = useState({});
    const [oldAccountCaseId, setOldAccountCaseId] = useState({});
    const [oldAccountCardId, setOldAccountCardId] = useState({});
    const [fade, setFade] = useState(false);
    const [subAccountantOptions, setSubAccountantOptions] = useState([]);
    const [creditAccountOptions, setCreditAccountOptions] = useState([]);
    const [debitAccountOptions, setDebitAccountOptions] = useState([]);
    const [accountCardOptions, setAccountCardOptions] = useState([]);
    const [accountCardEditDownloadOptions, setAccountCardEditDownloadOptions] = useState([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [itemIdToDelete, setItemIdToDelete] = useState({ itemId: null, type: null });
    const [sortCodeIdOptions, setSortCodeIdOptions] = useState([]);
    const [statusCheck, setStatusCheck] = useState(false);
    const [editedAddAccountCard, setEditedAddAccountCard] = useState(false);
    const [clientItems, setClientItems] = useState([]);
    const [formattedPalCodeOptions, setFormattedPalCodeOptions] = useState([]);
    const [assetGroups, setAssetGroups] = useState([]);
    const [assets, setAssets] = useState([]);
    const [showChooseAsset, setShowChooseAsset] = useState(false);
    const [itemToAddToAsset, setItemToAddToAsset] = useState('');
    const [itemForAssets, setItemForAssets] = useState({});
    const [saleEquipment, setSaleEquipment] = useState(false);
    const [dateForAsset, setDateForAsset] = useState(new Date().toISOString());
    const [clickedAsset, setClickedAsset] = useState(null);
    // For referencing the HTML to convert to PDF
    const vatContentRef = useRef(null);
    const code1RequestIdRef = useRef(0);
    const initializedItemKeyRef = useRef(null);
    const toNum = (v) => (v === null || v === undefined || v === '') ? 0 : Number(v);

    const refreshCode1Adjustments = (caseId, start, end, baseIncomesNoVats) => {
        if (!caseId || !token || !start || !end) return;
        code1RequestIdRef.current += 1;
        const thisRunId = code1RequestIdRef.current;

        ReportsService.getVatReportCode1Adjustments(caseId, start, end, token)
            .then(({ incomeNetCode1 }) => {
                if (thisRunId !== code1RequestIdRef.current) return;
                const add = Number(incomeNetCode1) || 0;
                if (add !== 0) {
                    setHasVatControlAccountEntries(true);
                    setCode1AdjustmentAmount(add);
                    const newIncomesNoVats = (baseIncomesNoVats ?? 0) + add;
                    setVatReport((prev) => {
                        const vatTransactions = Number(prev.VATTransactions) || 0;
                        const vatRatePct = newIncomesNoVats > 0 && vatTransactions
                            ? (vatTransactions / newIncomesNoVats) * 100
                            : (prev.VATRateTransactions != null ? Number(prev.VATRateTransactions) : 0);
                        return {
                            ...prev,
                            IncomesNoVats: newIncomesNoVats,
                            VATRateTransactions: typeof prev.VATRateTransactions === 'string'
                                ? String(vatRatePct.toFixed(2))
                                : vatRatePct
                        };
                    });
                } else {
                    setHasVatControlAccountEntries(false);
                    setCode1AdjustmentAmount(0);
                }
            })
            .catch(() => { });
    };

    // ======= PCN FUNCTIONALITY =======
    const {
        // PCN state from HamburgerMenuContext
        showPCNModal,
        setShowPCNModal,
        showPCNChecksModal,
        setShowPCNChecksModal,
        pcnHighConfirmationDocs,
        setPcnHighConfirmationDocs,
        pcnDocumentsNeedingAllocation,
        setPcnDocumentsNeedingAllocation,
        pcnDocumentsWithoutReference,
        setPcnDocumentsWithoutReference,
        pcnDocumentsOutOfRange,
        setPcnDocumentsOutOfRange,
        pcnChecksLoading,
        setPcnChecksLoading,
        pcnReportDetails,
        setPcnReportDetails,
        pcnIncludeStornoChecks,
        setPcnIncludeStornoChecks,
        handlePCNDownload,
        handlePCNChecks,
        handleDocumentRowClick,
    } = useHamburgerMenu();

    // PCN date state
    const [pcnDate, setPcnDate] = useState(dayjs());
    const [pcnStartDate, setPcnStartDate] = useState(dayjs());
    const [pcnEndDate, setPcnEndDate] = useState(dayjs());
    const [pcnIsDateRange, setPcnIsDateRange] = useState(false);

    // Handle document row click to send to hamburger menu
    const handleDocumentRowClickInVatModal = (doc) => {
        // Send document information to hamburger menu for processing
        if (doc && handleDocumentRowClick) {
            // Call the hamburger menu callback with document ID and document type
            handleDocumentRowClick(doc.id, doc.documentType);
        } else {
            console.warn('onDocumentRowClick is not available or doc is missing:', { doc, handleDocumentRowClickInVatModal });
        }
    };

    // Wrapper function to pass modal dates to handlePCNDownload
    const handlePCNDownloadWithModalDates = async () => {
        // Create a temporary setDates function for the modal context
        const setDates = (isRange, date, startDate, endDate) => {
            if (isRange) {
                return { start: startDate, end: endDate };
            } else {
                return { start: date, end: date };
            }
        };

        // Get the dates from the modal state
        const { start, end } = setDates(pcnIsDateRange, pcnDate, pcnStartDate, pcnEndDate);

        // Call the original handlePCNDownload with the modal dates
        await handlePCNDownload(start, end);
    };

    // Wrapper function to pass modal dates to handlePCNChecks
    const handlePCNChecksWithModalDates = async () => {
        // Create a temporary setDates function for the modal context
        const setDates = (isRange, date, startDate, endDate) => {
            if (isRange) {
                return { start: startDate, end: endDate };
            } else {
                return { start: date, end: date };
            }
        };

        // Get the dates from the modal state
        const { start, end } = setDates(pcnIsDateRange, pcnDate, pcnStartDate, pcnEndDate);

        // Call the original handlePCNChecks with the modal dates
        await handlePCNChecks(start, end);
    };


    // ======= USE EFFECT: BUILD / UPDATE vatReport =======
    useEffect(() => {
        if (!open) { initializedItemKeyRef.current = null; return; }
        if (!item) return;

        const itemKey = `${item.id ?? ''}_${item.clientCaseId ?? ''}_${item.StartDate}_${item.EndDate}_${item.VATTransactions}_${item.IncomesNoVats}`;
        const isFirstInit = initializedItemKeyRef.current === null;
        const hasChanged = initializedItemKeyRef.current !== itemKey;
        if (!hasChanged) return;
        initializedItemKeyRef.current = itemKey;

        const {
            id, clientName, clientLocation, clientCaseId, vatClientNumber, clientAccountantId,
            VATEquipment, VATExpenses, VATTransactions, VATNet,
            IncomesNoVats, IncomesNoZeroVats, StartDate, EndDate,
            VATRateTransactions,
            VATEquipment17, VATEquipment18, VATExpenses17, VATExpenses18,
            VATTransactions17, VATTransactions18, income17Amount, income18Amount
        } = item;

        const builtReport = {
            id: id ?? null,
            clientName: clientName ?? '',
            clientLocation: clientLocation ?? '',
            clientCaseId: clientCaseId ?? '',
            // DISPLAY ONLY: VAT-authority dealer number; falls back to clientCaseId when not set.
            vatClientNumber: vatClientNumber ?? '',
            VATEquipment: toNum(VATEquipment) ?? 0,
            VATExpenses: toNum(VATExpenses) ?? 0,
            VATTransactions: toNum(VATTransactions) ?? 0,
            VATNet: toNum(VATNet) ?? 0,
            IncomesNoVats: toNum(IncomesNoVats) ?? 0,
            IncomesNoZeroVats: toNum(IncomesNoZeroVats) ?? 0,
            StartDate: StartDate ?? new Date(),
            EndDate: EndDate ?? new Date(),
            VATRateTransactions: VATRateTransactions ?? '0',
            VATEquipment17: toNum(VATEquipment17) || 0,
            VATEquipment18: toNum(VATEquipment18) || 0,
            VATExpenses17: toNum(VATExpenses17) || 0,
            VATExpenses18: toNum(VATExpenses18) || 0,
            VATTransactions17: toNum(VATTransactions17) || 0,
            VATTransactions18: toNum(VATTransactions18) || 0,
            income17Amount: toNum(income17Amount) || 0,
            income18Amount: toNum(income18Amount) || 0
        };

        setVatReport(builtReport);
        setHasVatControlAccountEntries(false);
        setCode1AdjustmentAmount(0);

        // Only fetch enums on first open, not on date changes
        if (isFirstInit) {
            const clientObj = { caseId: clientCaseId, accountantId: clientAccountantId };
            fetchEnums(user, token, clientObj);
        }

        refreshCode1Adjustments(
            clientCaseId,
            StartDate ?? builtReport.StartDate,
            EndDate ?? builtReport.EndDate,
            Number(builtReport.IncomesNoVats) || 0
        );
    }, [item, open, token]);

    // ======= HANDLE CUSTOM EVENTS (vatUpdate) =======
    useEffect(() => {
        const handleVatMessage = (event) => {
            try {
                const message = JSON.parse(event.detail);
                if (message.source === 'vatReport') {
                    switch (message.eventName) {
                        case 'updateVatTransactions':
                            handleChangeVatReport("VATTransactions", message.value);
                            break;
                        case 'updateVatTransactions18':
                            handleChangeVatReport('VATTransactions18', message.value);
                            break;
                        case 'updateVatEquipment':
                            handleChangeVatReport("VATEquipment", message.value);
                            break;
                        case 'updateVatExpenses':
                            handleChangeVatReport("VATExpenses", message.value);
                            break;
                        case 'updateIncomesNoVats':
                            handleChangeVatReport("IncomesNoVats", message.value);
                            break;
                        case 'updateIncomesNoZeroVats':
                            handleChangeVatReport("IncomesNoZeroVats", message.value);
                            break;
                        default:
                            break;
                    }
                }
            } catch (err) {
                console.error("Failed to parse vatUpdate event:", event.detail);
            }
        };

        window.addEventListener('vatUpdate', handleVatMessage);
        return () => {
            window.removeEventListener('vatUpdate', handleVatMessage);
        };
    }, []);

    // ======= HELPER: UPDATE FIELDS (and recalc Net) =======
    const handleChangeVatReport = (field, val) => {
        const newValue = parseFloat(val) || 0;
        setVatReport((prev) => {
            let updated = { ...prev, [field]: newValue };

            // If user changed IncomesNoVats => recalc VATTransactions, VATTransactions17, and VATTransactions18
            if (field === 'IncomesNoVats') {
                const reportDate = updated.EndDate ? new Date(updated.EndDate) : new Date();
                const cutoffDate = new Date('2025-01-01');

                // Determine VAT rate based on date
                const vatRate = getVatValue(reportDate);
                updated.VATTransactions = +(newValue * vatRate).toFixed(2);

                // Split between 17% and 18% based on date
                if (reportDate >= cutoffDate) {
                    // After 2025-01-01: use 18%
                    updated.VATTransactions18 = updated.VATTransactions;
                    updated.income18Amount = newValue;
                } else {
                    // Before 2025-01-01: use 17%
                    updated.VATTransactions17 = updated.VATTransactions;
                    updated.income17Amount = newValue;
                }
            }

            // If user changed VATTransactions18 => check date and route to VATTransactions17 if before 2025-01-01
            if (field === 'VATTransactions18') {
                const reportDate = updated.EndDate ? new Date(updated.EndDate) : new Date();
                const cutoffDate = new Date('2025-01-01');

                if (reportDate < cutoffDate) {
                    // Before 2025-01-01: route the change to VATTransactions17 instead
                    updated.VATTransactions17 = newValue;
                    // Recalculate income17Amount
                    const vatRate = getVatValue(reportDate); // This will be 0.17 for dates before 2025-01-01
                    updated.income17Amount = +(newValue / vatRate).toFixed(2);
                    // Preserve existing VATTransactions18 if it has calculated values - don't reset it
                } else {
                    // After 2025-01-01: keep in VATTransactions18
                    updated.VATTransactions18 = newValue;
                    const vatRate = getVatValue(reportDate); // This will be 0.18 for dates after 2025-01-01
                    updated.income18Amount = +(newValue / vatRate).toFixed(2);
                    // Preserve existing VATTransactions17 if it has calculated values - don't reset it
                }
                // Update total VATTransactions
                updated.VATTransactions = +((updated.VATTransactions17 || 0) + (updated.VATTransactions18 || 0)).toFixed(2);
            }

            // If user changed VATTransactions17 => check date and route to VATTransactions18 if after 2025-01-01
            if (field === 'VATTransactions17') {
                const reportDate = updated.EndDate ? new Date(updated.EndDate) : new Date();
                const cutoffDate = new Date('2025-01-01');

                if (reportDate >= cutoffDate) {
                    // After 2025-01-01: route the change to VATTransactions18 instead
                    updated.VATTransactions18 = newValue;
                    // Recalculate income18Amount
                    const vatRate = getVatValue(reportDate); // This will be 0.18 for dates after 2025-01-01
                    updated.income18Amount = +(newValue / vatRate).toFixed(2);
                    // Preserve existing VATTransactions17 if it has calculated values - don't reset it
                } else {
                    // Before 2025-01-01: keep in VATTransactions17
                    updated.VATTransactions17 = newValue;
                    const vatRate = getVatValue(reportDate); // This will be 0.17 for dates before 2025-01-01
                    updated.income17Amount = +(newValue / vatRate).toFixed(2);
                    // Preserve existing VATTransactions18 if it has calculated values - don't reset it
                }
                // Update total VATTransactions
                updated.VATTransactions = +((updated.VATTransactions17 || 0) + (updated.VATTransactions18 || 0)).toFixed(2);
            }

            // If user changed VATTransactions => update total but preserve 17/18 split
            if (field === 'VATTransactions') {
                // Just update the total, don't recalculate 17/18 split
                // The split should be maintained from previous calculations
            }

            // Always recalc the Net
            updated.VATNet = +(
                (updated.VATTransactions || 0) -
                ((updated.VATEquipment || 0) + (updated.VATExpenses || 0))
            ).toFixed(2);

            return updated;
        });
    };
    const isEmptyObject = (obj) => {
        return Object.keys(obj).length === 0 && obj.constructor === Object;
    };


    // ======= HELPER: GENERATE HTML =======
    const generateVatHtml = () => {
        if (isEmptyObject(vatReport)) {
            return;
        }
        const israelTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const israelTime = new Date().toLocaleString('he-IL', { timeZone: israelTimeZone });
        const formattedDate = new Date().toLocaleString('he-IL', { timeZone: israelTimeZone, dateStyle: 'short', timeStyle: 'medium' });
        const startDateFormatted = new Date(vatReport.StartDate).toLocaleString('he-IL', { month: 'long' });
        const endDateFormatted = new Date(
            new Date(vatReport.EndDate).getFullYear(),
            new Date(vatReport.StartDate).getMonth() !== new Date(vatReport.EndDate).getMonth()
                ? new Date(vatReport.EndDate).getMonth() - 1
                : new Date(vatReport.EndDate).getMonth(),
            1
        ).toLocaleString('he-IL', { month: 'long', year: 'numeric' });
        const dateRange = `${startDateFormatted} - ${endDateFormatted}`;



        const htmlContent = `
         <!DOCTYPE html>
         <html lang='en'>
         <head>
             <meta charset='UTF-8' />
             <meta http-equiv='X-UA-Compatible' content='IE=edge' />
             <meta name='viewport' content='width=device-width, initial-scale=1.0' />
             <style>
                 a, button, input, select, h1, h2, h3, h4, h5, * {
                     box-sizing: border-box !important;
                     margin: 0;
                     padding: 0;
                     border: none;
                     text-decoration: none;
                     background: none;
                     -webkit-font-smoothing: antialiased;
                 }
                 menu, ol, ul {
                     list-style-type: none;
                     margin: 0;
                     padding: 0;
                 }
                 .body {
                     width: 100%;
                     display: flex;
                     justify-content: center;
                     align-items: center;
                 }
                 .container {
                     width: 1288px;
                     transform-origin: top center;
                     transform: scale(var(--vat-scale, 0.68));
                     display: flex;
                     flex-direction: column;
                     align-items: center;
                 }
                 .header-vat {
                     width: 1288px;
                     text-align: center;
                     padding: 6px 0;
                     background: linear-gradient(135deg, #f5f7ff 0%, #eef0ff 100%);
                     border-radius: 8px 8px 0 0;
                 }
                 .header_container {
                     display: flex;
                     justify-content: space-between;
                     align-items: center;
                 }
                 .date, .title, .placeholder {
                     flex: 1;
                     text-align: center;
                 }
                 .title {
                     font-size: 22px;
                     font-weight: 700;
                     color: #304FFF;
                 }
                 .vat_percentage, .date {
                     font-size: 18px;
                     padding: 3px 0;
                     color: #333;
                     font-weight: 600;
                 }
                 .frame-1 {
                     box-sizing: border-box;
                     position: relative;
                     background: white;
                     box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
                 }
                 .vat-report-1-fotor-2024071611375-1 {
                     width: 1288px;
                     height: 584px;
                     object-fit: contain;
                 }
                 .clientName, .VATEquipment, .VATExpenses, .VATNet, .IncomesNoVats, .IncomesNoZeroVats, .clientLocation, .dateRange, .clientCaseId, .VATTransactions {
                     color: black;
                     direction: rtl;
                     font-family: 'Inter-Regular', sans-serif;
                     font-weight: bold !important;
                     position: absolute;
                     word-wrap: break-word;
                     font-size: 22px !important;
                 }
                 [class^='clientName'] {
                     overflow: hidden !important;
                     text-overflow: ellipsis !important;
                     white-space: nowrap !important;
                 }
                 .new-content {}
                 .clientName {
                     text-align: right;
                     font-family: 'Inter-Regular', sans-serif;
                     font-size: 26px;
                     font-weight: 400;
                     position: absolute;
                     left: 706px;
                     top: 58px;
                     width: 464px;
                     height: 34px;
                 }
                 .VATEquipment {
                     text-align: right;
                     font-family: 'Inter-Regular', sans-serif;
                     font-size: 26px;
                     font-weight: 400;
                     position: absolute;
                     left: 881px;
                     top: 220px;
                     width: 333px;
                     height: 34px;
                 }
                 .VATExpenses {
                     text-align: right;
                     font-family: 'Inter-Regular', sans-serif;
                     font-size: 26px;
                     font-weight: 400;
                     position: absolute;
                     left: 881px;
                     top: 281px;
                     width: 333px;
                     height: 34px;
                 }
                 .VATNet {
                     text-align: right;
                     font-family: 'Inter-Regular', sans-serif;
                     font-size: 26px;
                     font-weight: 400;
                     position: absolute;
                     left: 881px;
                     top: 455px;
                     width: 333px;
                     height: 34px;
                 }
                 .IncomesNoVats {
                     text-align: right;
                     font-family: 'Inter-Regular', sans-serif;
                     font-size: 26px;
                     font-weight: 400;
                     position: absolute;
                     left: 432px;
                     top: 159px;
                     width: 333px;
                     height: 34px;
                 }
                 .IncomesNoZeroVats {
                     text-align: right;
                     font-family: 'Inter-Regular', sans-serif;
                     font-size: 26px;
                     font-weight: 400;
                     position: absolute;
                     left: 59px;
                     top: 159px;
                     width: 304px;
                     height: 34px;
                 }
                 .clientLocation {
                     text-align: right;
                     font-family: 'Inter-Regular', sans-serif;
                     font-size: 26px;
                     font-weight: 400;
                     position: absolute;
                     left: 250px;
                     top: 58px;
                     height: 34px;
                     width: 445px;
                 }
                 .dateRange {
                     text-align: right;
                     font-family: 'Inter-Regular', sans-serif;
                     font-size: 26px;
                     font-weight: 400;
                     position: absolute;
                     left: 873px;
                     top: 92px;
                     width: 266px;
                     height: 34px;
                 }
                 .clientCaseId {
                     text-align: right;
                     font-family: 'Inter-Regular', sans-serif;
                     font-size: 26px;
                     font-weight: 400;
                     position: absolute;
                     left: 40px;
                     top: 92px;
                     width: 244px;
                     height: 25px;
                 }
                 .VATTransactions {
                     text-align: right;
                     font-family: 'Inter-Regular', sans-serif;
                     font-size: 26px;
                     font-weight: 400;
                     position: absolute;
                     left: 881px;
                     top: 159px;
                     width: 333px;
                     height: 34px;
                 }
                 .color-text {
                     color: #304FFF;
                     font-weight: bold;
                 }
             </style>
             <title>Document</title>
         </head>
         <body>
            <div class='container'>
                <div class='header-vat'>
                     <div class='header_container'>
                         <div class='date'>${formattedDate}</div>
                         <div class='title'>דוח מס ערך מוסף</div>
                         <div class='placeholder'></div>
                     </div>
                     <div class='vat_percentage'><span class='color-text'>${vatReport.VATRateTransactions}%</span> = שיעור מע״מ עסקאות</div>
                 </div>
                <div class='frame-1'>
                
                     <img class='vat-report-1-fotor-2024071611375-1' src='${vatImage}' />
                     <div class='clientName'>${vatReport.clientName}</div>
                    <div class="tooltip-container">
                 <div class="tooltip tooltip-equipment">מע״מ 17%: ${roundVatToInteger(vatReport.VATEquipment17)}, מע״מ 18%: ${roundVatToInteger(vatReport.VATEquipment18)}</div>
                    <input 
                        class='VATEquipment input-field_Container_required-asterisk' 
                        value="${roundVatToInteger(vatReport.VATEquipment)}"
                        onfocus="this.value=this.value.replace(/,/g, '')"
                        onblur="this.value=parseFloat(this.value).toLocaleString('en-US')"
                        onchange="window.parent.dispatchEvent(new CustomEvent('vatUpdate', { detail: JSON.stringify({ source: 'vatReport', eventName: 'updateVatEquipment', value: this.value }) }))"
                    />
                     </div>   
 <div class='vat-html-preview-buttons'>
                   <div class='button-group'>
                       <button onclick="window.parent.dispatchEvent(new CustomEvent('openHtmlViewer', { detail: { type: 3 } }))" title="${vatUnprocessedCounts.exempt > 0 ? `${t('ExemptTransactions')} – ${t('UnprocessedItems')}: ${vatUnprocessedCounts.exempt}` : t('ExemptTransactions')}">
                           <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="9" y1="15" x2="15" y2="15"/></svg>
                           ${t('ExemptTransactionsDetails')}
                           ${vatUnprocessedCounts.exempt > 0 ? `<span class='unprocessed-badge'>${vatUnprocessedCounts.exempt}</span>` : ''}
                       </button>
                       <button onclick="window.parent.dispatchEvent(new CustomEvent('openHtmlViewer', { detail: { type: 0 } }))" title="${vatUnprocessedCounts.income > 0 ? `${t('PreviewIncomeVATInfo')} – ${t('UnprocessedItems')}: ${vatUnprocessedCounts.income}` : t('PreviewIncomeVATInfo')}">
                           <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
                           ${t('PreviewIncomeVAT')}
                           ${vatUnprocessedCounts.income > 0 ? `<span class='unprocessed-badge'>${vatUnprocessedCounts.income}</span>` : ''}
                       </button>
                       <button onclick="window.parent.dispatchEvent(new CustomEvent('openHtmlViewer', { detail: { type: 1 } }))" title="${vatUnprocessedCounts.expense > 0 ? `${t('PreviewExpenseVATInfo')} – ${t('UnprocessedItems')}: ${vatUnprocessedCounts.expense}` : t('PreviewExpenseVATInfo')}">
                           <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>
                           ${t('PreviewExpenseVAT')}
                           ${vatUnprocessedCounts.expense > 0 ? `<span class='unprocessed-badge'>${vatUnprocessedCounts.expense}</span>` : ''}
                       </button>
                       <button onclick="window.parent.dispatchEvent(new CustomEvent('openHtmlViewer', { detail: { type: 2 } }))" title="${vatUnprocessedCounts.asset > 0 ? `${t('PreviewAssetVATInfo')} – ${t('UnprocessedItems')}: ${vatUnprocessedCounts.asset}` : t('PreviewAssetVATInfo')}">
                           <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>
                           ${t('PreviewAssetVAT')}
                           ${vatUnprocessedCounts.asset > 0 ? `<span class='unprocessed-badge'>${vatUnprocessedCounts.asset}</span>` : ''}
                       </button>
                   </div>
               </div>
                    <div class="tooltip-container">
                        <div class="tooltip tooltip-expenses">מע״מ 17%: ${roundVatToInteger(vatReport.VATExpenses17)}, מע״מ 18%: ${roundVatToInteger(vatReport.VATExpenses18)}</div>
                        <input 
                            class='VATExpenses input-field_Container_required-asterisk' 
                            value="${roundVatToInteger(vatReport.VATExpenses)}"
                            onfocus="this.value=this.value.replace(/,/g, '')"
                            onblur="this.value=parseFloat(this.value).toLocaleString('en-US')"
                            onchange="window.parent.dispatchEvent(new CustomEvent('vatUpdate', { detail: JSON.stringify({ source: 'vatReport', eventName: 'updateVatExpenses', value: this.value }) }))"
                        />
                     </div>

                    <div class='VATNet'>${roundVatToInteger(vatReport.VATNet)}</div>
                     <input class='IncomesNoVats input-field_Container_required-asterisk' value="${roundVatToInteger(vatReport.IncomesNoVats)}" onfocus="this.value=this.value.replace(/,/g, '')" onblur="this.value=parseFloat(this.value).toLocaleString('en-US')" onchange="window.parent.dispatchEvent(new CustomEvent('vatUpdate', { detail: JSON.stringify({ source: 'vatReport', eventName: 'updateIncomesNoVats', value: this.value }) }))" />
                     <input class='IncomesNoZeroVats input-field_Container_required-asterisk' value="${roundVatToInteger(vatReport.IncomesNoZeroVats)}" onfocus="this.value=this.value.replace(/,/g, '')" onblur="this.value=parseFloat(this.value).toLocaleString('en-US')" onchange="window.parent.dispatchEvent(new CustomEvent('vatUpdate', { detail: JSON.stringify({ source: 'vatReport', eventName: 'updateIncomesNoZeroVats', value: this.value }) }))" />
                     <div class='clientLocation'>${vatReport.clientLocation}</div>
                     <div class='dateRange'>${dateRange}</div>
                     <div class='clientCaseId'>${vatReport.vatClientNumber || vatReport.clientCaseId}</div>

                    <div class="tooltip-container">
                        <div class="tooltip tooltip-transactions">מע״מ 17: ${roundVatToInteger(vatReport.VATTransactions17)}, מע״מ 18%: ${roundVatToInteger(vatReport.VATTransactions18)}</div>
                        <input
                        class="VATTransactions input-field_Container_required-asterisk"
                        value="${roundVatToInteger(vatReport.VATTransactions)}"
                        onfocus="this.value=this.value.replace(/,/g,'')"
                        onblur="this.value=parseFloat(this.value).toLocaleString('en-US')"
                        onchange="
                            /* overall VAT Transactions */
                            window.parent.dispatchEvent(
                            new CustomEvent('vatUpdate',{
                                detail: JSON.stringify({
                                source:'vatReport',
                                eventName:'updateVatTransactions',
                                value:this.value
                                })
                            })
                            );

                            /* 18 % bucket */
                            window.parent.dispatchEvent(
                            new CustomEvent('vatUpdate',{
                                detail: JSON.stringify({
                                source:'vatReport',
                                eventName:'updateVatTransactions18',
                                value:this.value
                                })
                            })
                            );
                        "
                        />
                        
                 </div>
               
                </div>
            </div>

                <div class='vat-breakdown'>
    <table class='vat-breakdown-table'>
        <thead>
            <tr>
                <th>18%</th>
                <th>17%</th>
                <th>${t('Category')}</th>
            </tr>
        </thead>
        <tbody>
            <tr>
                <td>${beautifyNumber(vatReport.VATEquipment18)}</td>
                <td>${beautifyNumber(vatReport.VATEquipment17)}</td>
                <td>${t('VATEquipment')}</td>
            </tr>
            <tr>
                <td>${beautifyNumber(vatReport.VATExpenses18)}</td>
                <td>${beautifyNumber(vatReport.VATExpenses17)}</td>
                <td>${t('VATExpenses')}</td>
            </tr>
            <tr>
                <td>${beautifyNumber(vatReport.VATTransactions18)}</td>
                <td>${beautifyNumber(vatReport.VATTransactions17)}</td>
                <td>${t('VATTransactions')}</td>
            </tr>
        </tbody>
    </table>


         </body>
         </html>`;
        return htmlContent;
    };

    // Per–VAT-type unprocessed counts (income / expense / asset / exempt). Drives
    // the red badges on the preview buttons + the inline warning alert directly
    // below the buttons inside the VAT preview HTML.
    const [vatUnprocessedCounts, setVatUnprocessedCounts] = useState({
        income: 0,
        expense: 0,
        asset: 0,
        exempt: 0,
        total: 0,
    });
    const vatUnprocessedRequestIdRef = useRef(0);

    const [vatHtmlContent, setVatHtmlContent] = useState('');
    useEffect(() => {

        setVatHtmlContent(generateVatHtml());
    }, [vatReport, vatUnprocessedCounts, onlyProcessedDocuments, direction]);

    // Reference continuity check state
    const [referenceContinuityIssues, setReferenceContinuityIssues] = useState(null);
    const [referenceCheckLoading, setReferenceCheckLoading] = useState(false);
    const [showReferenceAlert, setShowReferenceAlert] = useState(true);

    // Check reference continuity when modal opens or dates change
    useEffect(() => {
        if (!open || !vatReport?.clientCaseId || !token) return;

        const checkReferenceContinuity = async () => {
            setReferenceCheckLoading(true);
            try {
                let startDateToCheck, endDateToCheck;

                if (isRange) {
                    startDateToCheck = startDate;
                    endDateToCheck = endDate;
                } else if (isBiMonthly) {
                    startDateToCheck = biStartDate;
                    endDateToCheck = biEndDate;
                } else {
                    startDateToCheck = date;
                    endDateToCheck = date;
                }

                if (!startDateToCheck || !endDateToCheck) {
                    setReferenceContinuityIssues(null);
                    setReferenceCheckLoading(false);
                    return;
                }

                // Check reference sequence across the full year, not the selected period
                const { start, end } = getFullYearDateRange(startDateToCheck);

                const result = await DocumentService.CheckReferenceContinuity(
                    vatReport.clientCaseId,
                    start,
                    end,
                    token
                );

                setReferenceContinuityIssues(result);
            } catch (error) {
                console.error('Error checking reference continuity:', error);
                setReferenceContinuityIssues(null);
            } finally {
                setReferenceCheckLoading(false);
            }
        };

        // Small delay to avoid checking on every keystroke
        const timeoutId = setTimeout(checkReferenceContinuity, 500);
        return () => clearTimeout(timeoutId);
    }, [open, vatReport?.clientCaseId, token, isRange, isBiMonthly, date, startDate, endDate, biStartDate, biEndDate]);

    // Reset alert visibility when modal opens
    useEffect(() => {
        if (open) {
            setShowReferenceAlert(true);
        }
    }, [open]);

    // Fetch per-VAT-type unprocessed counts (income / expense / asset / exempt).
    // Re-runs whenever the modal opens or the period changes, mirroring how the
    // existing VatTransactionDocumentsExist swal is computed.
    useEffect(() => {
        if (!open || !vatReport?.clientCaseId || !token || !client?.accountantId) return;

        vatUnprocessedRequestIdRef.current += 1;
        const thisRunId = vatUnprocessedRequestIdRef.current;

        const fetchCounts = async () => {
            try {
                let startDateToCheck;
                let endDateToCheck;
                if (isRange) {
                    startDateToCheck = startDate;
                    endDateToCheck = endDate;
                } else if (isBiMonthly) {
                    startDateToCheck = biStartDate;
                    endDateToCheck = biEndDate;
                } else {
                    startDateToCheck = date;
                    endDateToCheck = date;
                }

                if (!startDateToCheck || !endDateToCheck) {
                    if (thisRunId !== vatUnprocessedRequestIdRef.current) return;
                    setVatUnprocessedCounts({ income: 0, expense: 0, asset: 0, exempt: 0, total: 0 });
                    return;
                }

                // Server normalises the month boundaries; isoString keeps tz consistent.
                const toIso = (d) => (d instanceof Date ? d : new Date(d)).toISOString();

                const res = await ClientService.VatTransactionDocumentsExist(
                    {
                        caseId: vatReport.clientCaseId,
                        accountantId: client.accountantId,
                        startDate: toIso(startDateToCheck),
                        endDate: toIso(endDateToCheck),
                    },
                    token,
                );
                if (thisRunId !== vatUnprocessedRequestIdRef.current) return;

                const data = res?.data || {};
                const income = Number(data.incomeVatUnprocessedCount) || 0;
                const expense = Number(data.expenseVatUnprocessedCount) || 0;
                const asset = Number(data.equipmentVatUnprocessedCount) || 0;
                const exempt = Number(data.zeroRatedVatUnprocessedCount) || 0;
                const total = Number(data.totalUnprocessedCount) || (income + expense + asset + exempt);
                setVatUnprocessedCounts({ income, expense, asset, exempt, total });
            } catch (err) {
                if (thisRunId !== vatUnprocessedRequestIdRef.current) return;
                console.error('Error fetching VAT unprocessed counts:', err);
                setVatUnprocessedCounts({ income: 0, expense: 0, asset: 0, exempt: 0, total: 0 });
            }
        };

        const timeoutId = setTimeout(fetchCounts, 300);
        return () => clearTimeout(timeoutId);
    }, [open, vatReport?.clientCaseId, client?.accountantId, token, isRange, isBiMonthly, date, startDate, endDate, biStartDate, biEndDate]);

    const canDownloadVatReport = async () => {
        let startDate = vatReport.StartDate;
        let endDate = vatReport.EndDate;

        if (client.reportingType === 'חודשי') {
            const base = dayjs.utc(startDate || endDate || new Date());
            startDate = base.startOf('month').format('YYYY-MM-DD');
            endDate = base.endOf('month').format('YYYY-MM-DD');
        }

        try {
            const existRes = await ClientService.VatTransactionDocumentsExist(
                {
                    caseId: vatReport.clientCaseId,
                    accountantId: client.accountantId,
                    startDate,
                    endDate,
                },
                token
            );

            const netVatMissing = existRes?.data?.netVat === false;

            if (netVatMissing) {
                const result = await MySwal.fire({
                    title: t('UnprocessedDocumentsWarning'),
                    text: t('SomeTransactionsAreStillNotProcessed'),
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: t('Yes'),
                    cancelButtonText: t('No'),
                    customClass: { container: 'swal-high-zindex' },
                });

                return result.isConfirmed;
            }

            return true; // everything is ready
        } catch (err) {
            toast.error(err.message || t('reportDownloadFailure'));
            return false;
        }
    };

    // ======= PDF DOWNLOAD =======
    const handleDownloadPdf = async (ref) => {
        const isReady = await canDownloadVatReport();
        if (!isReady) return;

        if (!ref?.current) return;

        // 1) Clone the original HTML node
        const clonedElement = ref.current.cloneNode(true);

        // 2) Remove or override the red border style.
        //    Example: removing the class "input-field_Container_required-asterisk"
        //    or manually overriding the border styles
        const inputs = clonedElement.querySelectorAll('input.input-field_Container_required-asterisk');
        inputs.forEach((input) => {
            // Either remove the class:
            input.classList.remove('input-field_Container_required-asterisk');

            // Optionally override inline styles (in case there's a border from CSS):
            input.style.border = '1px solid transparent';
            // or input.style.border = 'none';
        });

        // 3) Temporarily append this cloned element to the DOM so html2canvas can render it
        clonedElement.style.position = 'fixed';
        clonedElement.style.top = '-9999px'; // hide it
        document.body.appendChild(clonedElement);

        // 4) Use html2canvas on the cloned node
        html2canvas(clonedElement).then((canvas) => {
            const data = canvas.toDataURL('image/png');
            const doc = new jsPDF({ orientation: 'l', unit: 'px', format: 'a4' });
            const imgProps = doc.getImageProperties(data);
            const pdfWidth = doc.internal.pageSize.getWidth();
            const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
            doc.addImage(data, 'JPEG', 0, 0, pdfWidth, pdfHeight);
            doc.save('vatReport.pdf');

            // 5) Remove the cloned element after generating PDF
            document.body.removeChild(clonedElement);
        });
    };

    const openHtmlViewer = async (vatTypeNumber, options = {}) => {
        const { preserveEditId = null, preserveEditType = null } = options;
        try {

            const payload = {
                caseId: vatReport.clientCaseId,
                startDate: vatReport.StartDate,
                endDate: vatReport.EndDate,
                vatType: vatTypeNumber
            };

            const response = await ReportsService.vatTransactionHtml(payload, token);
            if (response.ok) {
                const result = await response.json();

                // 🏷️ Set title based on type
                let docTitle = '';
                switch (vatTypeNumber) {
                    case 0:
                        docTitle = t('IncomeVATTitle'); // מע״מ עסקאות
                        break;
                    case 1:
                        docTitle = t('ExpenseVATTitle'); // מע״מ תשומות
                        break;
                    case 2:
                        docTitle = t('AssetVATTitle'); // מע״מ תשומות ציוד
                        break;
                    case 3:
                        docTitle = t('ExemptTransactions'); // מע״מ תשומות ציוד
                        break;

                }

                const navigationList = parseVatHtmlNavigationRows(result.data);
                setVatHtmlTitle(docTitle); // assume you have this state: const [vatHtmlTitle, setVatHtmlTitle] = useState('');
                setVatTypeNumber(vatTypeNumber);
                setVatHtml(result.data);
                setVatHtmlNavigationList(navigationList);
                setIsHtmlModalOpen(true);

                // After refresh while editing: keep next/prev index on the same row
                if (preserveEditId != null) {
                    const nextIndex = navigationList.findIndex(
                        (row) =>
                            String(row.id) === String(preserveEditId) &&
                            (preserveEditType == null || String(row.type) === String(preserveEditType))
                    );
                    setCurrentVatEditIndex(nextIndex);
                }
            } else {
                toast.error(t('FailedToLoadVatTransactionHtml'));
            }
        } catch (err) {
            console.error("Failed to fetch VAT HTML:", err);
            toast.error(t('FailedToLoadVatTransactionHtml'));
        }
    };

    useEffect(() => {
        const handleVatHtmlPreview = (e) => {
            const { type } = e.detail;
            openHtmlViewer(type); // Call your actual function
        };

        window.addEventListener('openHtmlViewer', handleVatHtmlPreview);

        return () => {
            window.removeEventListener('openHtmlViewer', handleVatHtmlPreview);
        };
    }, [vatReport, token]); // Add dependencies to ensure up-to-date values

    // ======= MANUAL SAVE =======
    const saveReportTaxManually = async (reportTax) => {
        try {
            const statusValue = (reportingWay === 'unreported') ? 2 : 1;
            reportTax.status = statusValue;

            let response;

            response = await ReportsService.createReportedVat({
                caseId: reportTax.caseId,
                VATTransactions: reportTax.VATTransactions,
                OtherVATTaxes: reportTax.OtherVATTaxes,
                EquipmentVAT: reportTax.EquipmentVAT,
                NetVAT: reportTax.NetVAT,
                IncomesNoVat: reportTax.IncomesNoVat,
                IncomesNoZeroVats: reportTax.IncomesNoZeroVats,
                ExpensesNoVat: 0,
                EquipmentExpenses: 0,
                StartDate: reportTax.startDate,
                EndDate: reportTax.endDate
            }, token);
            if (response && response.ok) {
                const responseData = await response.json();
                toast.success(t('ReportedTaxUpdatedSuccess'));
                onDataUpdate(responseData);
                await closeReportingMonthsIfRequested(reportTax.caseId);
            } else {
                toast.error(t('ReportedTaxUpdateFailed'));
            }
        } catch (error) {
            console.error(t('Error saving reported tax:'), error);
            toast.error(t('ReportedTaxSaveFailed'));
        }
    };


    // ======= AUTOMATIC SEND TO TAX AUTHORITY =======
    const startAuthCompletionCheck = async (authWindow, scope) => {
        let attempts = 0;
        const maxAttempts = 25;

        const checkAuthCompletion = async () => {
            try {
                const result = await TaxAuthorityService.checkAuthCompletionAccountant(
                    user.accountantId,
                    scope,
                    token
                );
                if (result.completed) {
                    setTaxAuthorityData((prev) => ({
                        ...prev,
                        resumeTaxAuthorityVatOperation: true
                    }));
                    authWindow.close();
                } else {
                    attempts += 1;
                    if (attempts < maxAttempts) {
                        setTimeout(checkAuthCompletion, 3000);
                    } else {
                        setLoading(false);
                        console.error('Token retrieval failed after maximum attempts.');
                        toast.error('Token retrieval failed after maximum attempts.');
                        authWindow.close();
                    }
                }
            } catch (err) {
                console.error('Error checking auth completion:', err);
                toast.error(t('Error checking auth completion'));
                authWindow.close();
                setLoading(false);
            }
        };

        checkAuthCompletion();
    };

    // Pre-select the method the client already set up with the Tax Authority. A stored 0 means
    // nothing was chosen, and 0 is not a valid choice for a payment report, so fall back to direct debit.
    useEffect(() => {
        setPaymentMethod(
            client?.bank?.paymentMethod === TA_CREDIT_CARD ? TA_CREDIT_CARD : TA_DIRECT_DEBIT
        );
    }, [client?.bank?.paymentMethod]);

    // If we get the token or see resumeTaxAuthorityVatOperation = true:
    useEffect(() => {
        if (taxAuthorityData.resumeTaxAuthorityVatOperation) {
            // Actually send the data
            TaxAuthoritySendVatReport(taxAuthorityData.UpdatedAdd, token);
        }
    }, [taxAuthorityData.resumeTaxAuthorityVatOperation]);

    const TaxAuthoritySendVatReport = async (data, tokenParam) => {
        setLoading(true);
        const sendVatReportingDto = {
            CaseId: data.caseId,
            Date: data.endDate,
            VATTransactions: data.VATTransactions,
            OtherVATTaxes: data.OtherVATTaxes,
            EquipmentVAT: data.EquipmentVAT,
            NetVAT: data.NetVAT,
            IncomesNoVat: data.IncomesNoVat,
            Income17Amount: data.income17Amount,
            Income18Amount: data.income18Amount,
            VATTransactions17: data.VATTransactions17,
            VATTransactions18: data.VATTransactions18,
            IncomesNoZeroVats: data.IncomesNoZeroVats,
            AccountantId: user.accountantId,
            // Both fields only apply to a payment report; the server zeroes them for refund/zero reports.
            // Installments exist for direct debit only, so a card is always a single payment.
            NumberOfPayments: reportingWay === 'automatic'
                ? (paymentMethod === TA_CREDIT_CARD ? 1 : numberOfPayments)
                : null,
            PaymentMethod: reportingWay === 'automatic' ? paymentMethod : null
        };
        try {

            const response = await ReportsService.sendVatReporting(sendVatReportingDto, tokenParam);

            if (!response.ok) {
                const errorData = await response.json();

                // Extract relevant details
                const errorMessage = errorData.message || "Unknown error occurred.";
                const errorNumber = errorData.error?.errorNumber || "N/A";
                const supportMessage = errorData.error?.supportMessage || "No additional details.";

                // Format the error message
                const detailedErrorMessage = `${errorMessage}\n\n🔴 Error Number: ${errorNumber}\n💬 Support Message: ${supportMessage}`;

                throw {
                    message: detailedErrorMessage,
                    details: errorData
                };
            }

            const responseData = await response.json();

            // The card was charged but the Tax Authority failed to save the report, so the period
            // stays unreported and the accountant has to settle the charge before re-filing.
            if (responseData.cardChargedReportNotSaved) {
                toast.warning(
                    t('VatReportChargedNotFiled', {
                        caseId: data.caseId,
                        date: formatMonthYearDate(sendVatReportingDto.Date),
                        // The accountant needs these to reconcile the charge with the credit company.
                        reference: responseData.reference || '-',
                        amount: responseData.totalPayment ?? '-',
                        errorNumber: responseData.errorNumber ?? responseData.errorCode ?? '-'
                    }),
                    {
                        autoClose: false,
                    }
                );
                return;
            }

            toast.success(
                t('ReportedTaxSuccess', {
                    caseId: data.caseId,
                    date: formatMonthYearDate(sendVatReportingDto.Date)
                })
                ,
                {
                    autoClose: false,
                }
            );

            // Store in DB
            await saveReportTaxManually(data);
        } catch (error) {
            console.error("Full error details:", error.details);

            // Ensure error.details and error.details.error exist
            const errorDetails = error.details?.error || {}; // Extract the inner error object safely
            const errorNumber = errorDetails.errorNumber ?? "N/A"; // Default to "N/A" if not present
            const supportMessage = errorDetails.supportMessage ?? "No additional details."; // Default if missing

            const errorMessage = `${t('ReportedTaxError', {
                caseId: data.caseId,
                date: formatMonthYearDate(sendVatReportingDto.Date),
                errorNumber: errorNumber,
                supportMessage: supportMessage
            })}`;

            toast.error(errorMessage
                ,
                {
                    autoClose: false, // Toast remains until user clicks to dismiss
                }
            );
        } finally {
            onClose();
            setLoading(false);
        }
    };



    // ======= CONFIRMATION FLOW =======
    const handleConfirmVatReport = async () => {
        const { UpdatedAdd } = taxAuthorityData;
        setIsConfirmationVisible(false);
        try {
            const tokenData = await TaxAuthorityService.requestTokenAccountant(
                user.accountantId,
                Constants.TaxAuthorityScopes['VatReport'],
                token
            );
            if (tokenData?.authUrl) {
                setTaxAuthorityData((prev) => ({
                    ...prev,
                    authUrl: tokenData.authUrl,
                    resumeTaxAuthorityVatOperation: false
                }));
                const authWindow = window.open(tokenData.authUrl, '_blank');
                onClose(); // close the modal
                startAuthCompletionCheck(authWindow, Constants.TaxAuthorityScopes['VatReport']);
            } else if (tokenData?.accessToken) {
                await TaxAuthoritySendVatReport(UpdatedAdd, token);
            }
        } catch (err) {
            console.error('Error fetching token:', err);
            toast.error(t('ErrorFetchingToken'));
            setLoading(false);
        }
    };

    const handleConfirm = async () => {
        // For now, only one action type => 'VatReport'
        if (actionType === 'VatReport') {
            await handleConfirmVatReport();
        }
        else if (actionType === 'delete') {
            await handleConfirmDelete();
        }
    };


    const getConfirmMessage = () => {
        switch (actionType) {
            case 'VatReport':
                return t('AreYouSureSendVatReport');
            case 'delete':
                return t('confirmDeleteMessage');


        }
    };

    const getTitleMessage = () => {
        switch (actionType) {

            case 'VatReport':
                return t('ConfirmVatReport');
            case 'delete':
                return t('confirmDeleteTitle');

        }
    };

    const deleteVatReport = async (reportData, token) => {
        try {
            const response = await ReportsService.deleteReportedVatByPeriod({
                caseId: reportData.caseId,
                StartDate: reportData.startDate,
                EndDate: reportData.endDate,
            }, token);

            if (response && response.ok) {
                toast.success(t('ReportedVatDeletedSuccess'));
                const responseData = {
                    vatTransactions: 0,
                    otherVATTaxes: 0,
                    equipmentVAT: 0,
                    netVAT: 0,
                }
                onDataUpdate(responseData);

            } else {
                toast.error(t('ReportedVatDeleteFailed'));
            }
            onClose();
        } catch (error) {
            console.error(t('Error deleting VAT report:'), error);
            toast.error(t('ReportedVatDeleteFailed'));
        }
    };



    // ======= WHEN CLICK "SAVE" =======
    const handleSaveVatReport = async () => {

        let startDate = vatReport.StartDate;   // default: what the user picked
        let endDate = vatReport.EndDate;

        if (client.reportingType === 'חודשי') {
            const base = dayjs.utc(vatReport.StartDate || vatReport.EndDate || new Date());
            // Set startDate to first month of the same year
            startDate = base.startOf('year').format('YYYY-MM-DD'); // "2025-01-01"
            endDate = base.endOf('month').format('YYYY-MM-DD'); // "2025-06-30"
        }

        try {
            const existRes = await ClientService.VatTransactionDocumentsExist(
                {
                    caseId: vatReport.clientCaseId,   // same names as DTO
                    accountantId: client.accountantId,   // add this field if needed
                    startDate,
                    endDate,
                },
                token,
            );

            const netVatMissing = existRes?.data?.netVat === false;  // adjust if reversed

            if (netVatMissing) {
                const result = await MySwal.fire({
                    title: t('UnprocessedDocumentsWarning'),
                    text: t('SomeTransactionsAreStillNotProcessed'),
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: t('Yes'),
                    cancelButtonText: t('No'),
                    customClass: { container: 'swal-high-zindex' },
                });
            }
            /* 2️⃣  Check reference continuity first (full calendar year) */
            let referenceContinuityResult = null;
            try {
                const { start: referenceStartDate, end: referenceEndDate } = getFullYearDateRange(
                    vatReport.StartDate || vatReport.EndDate || startDate
                );
                referenceContinuityResult = await DocumentService.CheckReferenceContinuity(
                    vatReport.clientCaseId,
                    referenceStartDate,
                    referenceEndDate,
                    token
                );
            } catch (error) {
                console.error('Error checking reference continuity:', error);
                // Continue with the process even if reference check fails
            }


            /* 3️⃣  If reference continuity issues found, show warning */
            if (referenceContinuityResult && !referenceContinuityResult.isContinuous) {
                const issuesText = referenceContinuityResult.issues
                    .map(issue => {
                        let translatedDescription = issue.description;
                        if (issue.issueType === 'Duplicate') {
                            translatedDescription = t('Duplicate reference number') + ': ' + issue.actualReference;
                        } else if (issue.issueType === 'Gap') {
                            translatedDescription = t('Missing reference number') + ': ' + issue.expectedReference;
                        } else if (issue.issueType === 'Invalid') {
                            translatedDescription = t('Invalid reference format') + ': \'' + issue.actualReference + '\'';
                        }
                        return `• ${translatedDescription}`;
                    })
                    .join('\n');

                const result = await MySwal.fire({
                    title: t('ReferenceContinuityIssues'),
                    html: `
                        <div style="text-align: ${direction === 'rtl' ? 'right' : 'left'}; direction: ${direction};">
                            <p>${t('ReferenceContinuityWarning')}</p>
                            <div style="background: #f5f5f5; padding: 10px; border-radius: 5px; margin: 10px 0; max-height: 300px; overflow-y: auto; direction: ${direction}; text-align: ${direction === 'rtl' ? 'right' : 'left'};">
                                <strong>${t('IssuesFound')}</strong><br/>
                                ${issuesText.replace(/\n/g, '<br/>')}
                            </div>
                            <p>${t('DoYouWantToContinue')}</p>
                        </div>
                    `,
                    icon: 'warning',
                    showCancelButton: true,
                    showDenyButton: true,
                    confirmButtonText: t('Yes'),
                    cancelButtonText: t('No'),
                    denyButtonText: t('ViewIncomes'),
                    customClass: { container: 'swal-high-zindex' },
                });

                if (result.isDenied) {
                    // User clicked "View Incomes" - navigate to Incomes page with date range
                    if (viewDocumentDetails) {
                        viewDocumentDetails('Incomes');
                    }
                    return; // Exit the save process
                }

                if (!result.isConfirmed) {
                    return; // user aborted
                }
            }

            /* 4️⃣  If something is missing, confirm with SweetAlert       */

        } catch (err) {
            toast.error(err.message || t('reportDownloadFailure'));
            return;               // bail out on API failure
        }
        // Build the data object to store
        const UpdatedAdd = {
            id: vatReport.id,
            caseId: vatReport.clientCaseId,
            startDate: vatReport.StartDate,
            endDate: vatReport.EndDate,
            VATTransactions: vatReport.VATTransactions,
            OtherVATTaxes: vatReport.VATExpenses,
            EquipmentVAT: vatReport.VATEquipment,
            NetVAT: vatReport.VATNet,
            IncomesNoVat: vatReport.IncomesNoVats,
            income17Amount: vatReport.income17Amount,
            income18Amount: vatReport.income18Amount,
            VATTransactions17: vatReport.VATTransactions17 || 0,
            VATTransactions18: vatReport.VATTransactions18 || 0,
            IncomesNoZeroVats: vatReport.IncomesNoZeroVats
        };

        setTaxAuthorityData((prev) => ({ ...prev, UpdatedAdd }));

        if (reportingWay === 'unreported') {
            await deleteVatReport(UpdatedAdd, token);
        }
        else if (reportingWay === 'automatic') {
            setActionType('VatReport');
            setIsConfirmationVisible(true);
        } else {
            // manual or unreported => just save
            await saveReportTaxManually(UpdatedAdd);
            onClose();
        }
    };
    const handleVatRowClick = ({ cells, id, type }) => {
        const index = vatHtmlNavigationList.findIndex(
            (row) => String(row.id) === String(id) && String(row.type) === String(type)
        );
        setCurrentVatEditIndex(index);
        viewDocument(id, type);
    };

    const handleOpenEditSingleEntry = (item) => {
        setEditedItem(item);
        setIsEditSingleEntryOpen(true);
    };

    const handleCancel = () => {
        setEditedItem({});
        setIsEditSingleEntryOpen(false);
        setCurrentVatEditIndex(-1);
    };

    const handleVatEditPrevious = () => {
        if (currentVatEditIndex <= 0 || vatHtmlNavigationList.length === 0) return;
        const newIndex = currentVatEditIndex - 1;
        const row = vatHtmlNavigationList[newIndex];
        if (!row) return;
        setCurrentVatEditIndex(newIndex);
        viewDocument(row.id, row.type);
    };

    const handleVatEditNext = () => {
        if (
            currentVatEditIndex < 0 ||
            currentVatEditIndex >= vatHtmlNavigationList.length - 1 ||
            vatHtmlNavigationList.length === 0
        ) return;
        const newIndex = currentVatEditIndex + 1;
        const row = vatHtmlNavigationList[newIndex];
        if (!row) return;
        setCurrentVatEditIndex(newIndex);
        viewDocument(row.id, row.type);
    };

    // ======= HANDLE OPEN COMPARATIVE (YEARLY) VAT REPORT =======
    const handleOpenComparativeVat = async () => {
        let startDate = vatReport.StartDate;   // default: what the user picked
        let endDate = vatReport.EndDate;

        if (client.reportingType === 'חודשי') {
            const base = dayjs.utc(vatReport.StartDate || vatReport.EndDate || new Date());
            startDate = base.startOf('month').format('YYYY-MM-DD'); // "2025-06-01"
            endDate = base.endOf('month').format('YYYY-MM-DD'); // "2025-06-30"
        }

        try {
            const existRes = await ClientService.VatTransactionDocumentsExist(
                {
                    caseId: vatReport.clientCaseId,   // same names as DTO
                    accountantId: client.accountantId,   // add this field if needed
                    startDate,
                    endDate,
                },
                token,
            );

            const netVatMissing = existRes?.data?.netVat === false;  // adjust if reversed

            /* 2️⃣  If something is missing, confirm with SweetAlert       */
            if (netVatMissing) {
                const result = await MySwal.fire({
                    title: t('UnprocessedDocumentsWarning'),
                    text: t('SomeTransactionsAreStillNotProcessed'),
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: t('Yes'),
                    cancelButtonText: t('No'),
                    customClass: { container: 'swal-high-zindex' },
                });

                if (!result.isConfirmed) {
                    return;           // user aborted – nothing else runs
                }
            }

            if (!vatReport.clientCaseId || !token) {
                toast.error(t("FailedToLoadYearlyVatReport"));
                return;
            }

            setShowYearlyVatReport(true);
        } catch (error) {
            console.error("Error opening yearly VAT report:", error);
            toast.error(t("FailedToLoadYearlyVatReport"));
        }
    };

    const accumulatedVatRequestIdRef = useRef(0);
    useEffect(() => {
        if (!vatReport.clientCaseId || !token) return;

        accumulatedVatRequestIdRef.current += 1;
        const thisRunId = accumulatedVatRequestIdRef.current;

        const fetchAccumulatedVat = async () => {
            try {
                const dateToFetch = isBiMonthly ? (biEndDate ?? new Date()) : (date ?? new Date());
                const data = await ReportsService.getAccumulatedDifferenceInVat(vatReport.clientCaseId, dateToFetch, token);
                if (thisRunId !== accumulatedVatRequestIdRef.current) return;
                setAccumulatedVat(data);
            } catch (error) {
                if (thisRunId !== accumulatedVatRequestIdRef.current) return;
                console.error("Error fetching accumulated VAT:", error);
                setAccumulatedVat(null);
            }
        };

        fetchAccumulatedVat();
    }, [vatReport.clientCaseId, vatReport.StartDate, vatReport.EndDate, token]);


    //edit modal functions

    const fetchEnumsInProgressRef = useRef(false);
    const fetchEnums = async (user, token, client, silent = false) => {
        if (!client) return;
        if (fetchEnumsInProgressRef.current) return;
        fetchEnumsInProgressRef.current = true;
        if (!silent) setLoading(true);
        try {
            // Initiate all fetch requests in parallel
            const [
                codeIdResponse,
                accountsResponse,
                clientItemsResponse,
                palCodesResponse,
            ] = await Promise.all([
                EnumsService.GetSortCodesByAccountantId(client.accountantId, token),
                EnumsService.GetAccountCardsByCaseId(client.caseId, token),
                EnumsService.GetClientItemsByCaseId(client.caseId, token),
                EnumsService.GetPalCodesByAccountantId(client.accountantId, token)
            ]);

            // Process sortCodeIdOptions if response is okay
            if (codeIdResponse.ok) {
                const codeIdData = await codeIdResponse.json();
                setSortCodeIdOptions(codeIdData); // Assuming codeIdData is already in the correct format
            }

            // Process account options if response is okay
            if (accountsResponse.ok) {
                const accountsData = await accountsResponse.json();
                setCreditAccountOptions(accountsData);
                setDebitAccountOptions(accountsData); // Assuming both options are the same
                setAccountCardEditDownloadOptions(accountsData);
                const accountCardOptions = [{ name: t('AddAccountCard'), value1: 'AddItem' }, ...accountsData];
                setAccountCardOptions(accountCardOptions);

            }

            // const assetResponse = await AssetService.GetAllAssets(client?.caseId, token);
            // if (assetResponse.success) {
            //     setAssets(assetResponse.data);
            // } else {
            //     toast.error(t('FailedToFetchAssets'));
            // }


            // Process client items if response is okay
            if (clientItemsResponse.ok) {
                const clientItemsData = await clientItemsResponse.json();
                clientItemsData.unshift({ makat: 'add-item', name: t('AddItem') });
                setClientItems(clientItemsData);
            }

            // Process palCodes if response is okay
            if (palCodesResponse.ok) {
                const palCodesData = await palCodesResponse.json();
                const formattedPalCodes = palCodesData.map(palCode => ({
                    label: palCode.palCodeId,
                    value: palCode.palCodeId
                }));
                setFormattedPalCodeOptions(formattedPalCodes);
            }

        } catch (error) {
            console.error("Error fetching enum data:", error);
            toast.error(t('enumFetchError', { errorText: error.message }));
        } finally {
            fetchEnumsInProgressRef.current = false;
            if (!silent) setLoading(false);
        }
    };


    const updateAccountFields = (newValue, accountType) => {


        const isIncomesDebit = editedItem.documentType === 'Incomes' && accountType === 'debitAccount';
        const isExpensesCredit = editedItem.documentType === 'Expenses' && accountType === 'creditAccount';

        const updatedFields = {
            accountCodeId: isIncomesDebit || isExpensesCredit ? (newValue ? newValue.cardId : '') : '',
            accountCodeName: isIncomesDebit || isExpensesCredit ? (newValue ? newValue.name : '') : '',
            accountCaseId: isIncomesDebit || isExpensesCredit ? (newValue ? newValue.accountCaseId : '') : '',
        };

        // Update the edited item with the new fields
        setEditedItem((prev) => ({
            ...prev,
            [accountType]: newValue ? newValue.cardId : '',
            ...(isIncomesDebit || isExpensesCredit ? updatedFields : {}),
        }));
    };

    const handleAccountChange = (selectedOption, field, CustomDropdownFlag = false) => {
        if (field === 'debitAccount' || field === 'creditAccount') {
            const accountCardEditDownload = accountCardEditDownloadOptions.find(option => option.cardId === selectedOption.value);
            const name = accountCardEditDownload?.name;
            const accountCaseId = accountCardEditDownload?.accountCaseId;

            editedItem.documentType === 'Incomes' ? setEditedItem({ ...editedItem, accountCodeName: name, accountCodeId: selectedOption.value, debitAccount: selectedOption.value, accountCaseId: accountCaseId }) : setEditedItem({ ...editedItem, accountCodeName: name, accountCodeId: selectedOption.value, creditAccount: selectedOption.value, accountCaseId: accountCaseId });
        } else if (field === 'accountCodeId') {
            if (selectedOption.value1 === 'AddItem') {
                setEditedAddAccountCard(true);
                setOldAccountName(editedItem.accountCodeName);
                setOldAccountCaseId(editedItem.accountCaseId);
                setOldAccountCardId(editedItem.accountCodeId);
                setEditedItem({ ...editedItem, accountCodeName: '', accountCodeId: '', accountCaseId: '' });
            }
            else {
                const accountCardEdit = accountCardEditDownloadOptions.find(option =>
                    option.cardId === (CustomDropdownFlag ? selectedOption.cardId : selectedOption.value)
                );
                const name = accountCardEdit?.name;
                const accountCaseId = accountCardEdit?.accountCaseId;
                const cardId = CustomDropdownFlag ? selectedOption.cardId : selectedOption.value;
                editedItem.documentType === 'Incomes' ? setEditedItem({ ...editedItem, accountCodeName: name, accountCodeId: cardId, debitAccount: cardId, accountCaseId: accountCaseId }) : setEditedItem({ ...editedItem, accountCodeName: name, accountCodeId: cardId, creditAccount: cardId, accountCaseId: accountCaseId });
            }
        }
    };

    const handleChange = (field, value) => {
        const updatedValue = value;

        if (field === 'sortCodeId') {



            if (!value || !sortCodeIdOptions.some((option) => option.sortCodeId === value)) {
                // Set default values for the empty state
                setEditedItem({
                    ...editedItem,
                    vat: 100, // Default VAT
                    [field]: '',
                });
                return; // Exit early since there's no valid sortCodeId
            }

            const name = sortCodeIdOptions.find(
                (option) => option.sortCodeId === value
            )?.sortCodeName || ''

            // Extract the name without the parentheses
            const nameWithoutParentheses = name.replace(/\s*\(.*?\)\s*/, '').trim();

            // Check for a number within parentheses
            const match = name.match(/\((\d+)%?/);
            let customVat = match ? parseInt(match[1], 10) : null;


            const sortCode = sortCodeIdOptions.filter(sc => sc.sortCodeId === updatedValue)[0];

            const newVat = sortCode?.vat ?? 100;


            const accountCard = accountCardOptions.find(
                (item) => item.cardId === (editedItem.documentType === 'Incomes' ? sortCode.debitAccount : sortCode.creditAccount)
            );

            const debit = accountCardOptions.find(
                (item) => item.cardId === sortCode.debitAccount
            );

            const credit = accountCardOptions.find(
                (item) => item.cardId === sortCode.creditAccount
            );



            if (value === '1') { //hova
                setEditedItem({
                    ...editedItem,
                    vat: newVat,
                    [field]: updatedValue,
                    creditAccount: null,
                    debitAccount: debit ? debit.cardId : editedItem?.debitAccount,
                    accountCodeName: accountCard ? accountCard.name : editedItem.accountCodeName,
                    accountCodeId: accountCard ? accountCard.cardId : editedItem.accountCodeId,
                    accountCaseId: accountCard ? accountCard.accountCaseId : editedItem.accountCaseId,
                });
                return;
            }
            if (value === '2') { //z5ot
                setEditedItem({
                    ...editedItem,
                    vat: newVat,
                    [field]: updatedValue,
                    creditAccount: credit ? credit.cardId : editedItem?.creditAccount,
                    debitAccount: null,
                    accountCodeName: accountCard ? accountCard.name : editedItem.accountCodeName,
                    accountCodeId: accountCard ? accountCard.cardId : editedItem.accountCodeId,
                    accountCaseId: accountCard ? accountCard.accountCaseId : editedItem.accountCaseId,
                });
                return;
            }

            if (editedItem.documentType === 'JournalEntries') {

                setEditedItem({
                    ...editedItem,
                    vat: newVat,
                    [field]: updatedValue,
                    creditAccount: sortCode.creditAccount ? sortCode.creditAccount : editedItem.creditAccount,
                    debitAccount: sortCode.debitAccount ? sortCode.debitAccount : editedItem.debitAccount,
                });
            }
            else {
                setEditedItem({
                    ...editedItem,
                    vat: newVat,
                    [field]: updatedValue,
                    creditAccount: credit ? credit.cardId : editedItem?.creditAccount,
                    debitAccount: debit ? debit.cardId : editedItem?.debitAccount,
                    accountCodeName: accountCard ? accountCard.name : editedItem.accountCodeName,
                    accountCodeId: accountCard ? accountCard.cardId : editedItem.accountCodeId,
                    accountCaseId: accountCard ? accountCard.accountCaseId : editedItem.accountCaseId,
                });

            }


        }
        else if (field === 'creditAccount' || field === 'debitAccount') {

            // Use the helper function to update account fields for credit or debit account
            const newValue = accountCardOptions.find(option => option.cardId === updatedValue) || null;
            updateAccountFields(newValue, field);
        }
        else {
            // For fields other than sortCodeId, just update the edited item
            setEditedItem({
                ...editedItem,
                [field]: updatedValue,
            });
        }
    };

    const handleRadioChange = async (e, options = {}) => {
        if (e.target.value?.toString() === 'done' && !options?.validationAlreadyDone) {

            const isValid = await validateDocumentBeforeProcessing(editedItem, t, client, options?.forceProcess === true);
            if (!isValid) return;
        }

        if (e.target.value?.toString() === 'done') {
            setStatusCheck(true);
        }
        else if (e.target.value?.toString() === 'notDone') {
            setStatusCheck(false);
        }

        setEditedItem({ ...editedItem, status: e.target.value });
    };

    const handleVatRadioChange = (e) => {
        setEditedItem({ ...editedItem, ["vat"]: parseFloat(e.target.value) });
    };

    const handleDateChange = (e, field) => {
        setEditedItem({ ...editedItem, [field]: e });
    };

    const setReportingMonthDate = async (selectedDate, field) => {
        if (!selectedDate) {
            return;
        }
        const newDate = new Date(Date.UTC(selectedDate.getFullYear(), selectedDate.getMonth(), 10));
        const response1 = await ClientService.CheckIfMonthClosed(client.caseId, newDate, token);
        if (response1.ok) {
            const isClosedReportingMonth = await response1.json();
            if (isClosedReportingMonth) {
                toast.info(t('MonthReportAlreadyClosedTryAgain'));
                return
            }
            else {
                setEditedItem({ ...editedItem, [field]: newDate });
            }
        }
    }
    const CustomInput2 = React.forwardRef(({ value, onClick }, ref) => (
        <button className="edit-custom-input" onClick={onClick} ref={ref}>
            {value}
        </button>
    ));

    const handleCloseEditAddNewAccountCardDocument = () => {
        setEditedItem({ ...editedItem, accountCodeName: oldAccountName, accountCodeId: oldAccountCardId, accountCaseId: oldAccountCaseId });
        setEditedAddAccountCard(false);
    };

    const handleEditAddAccountChange = (selectedOption, field) => {
        if (field === 'AccountCodeId') {
            editedItem.documentType === 'Incomes' ? setEditedItem({ ...editedItem, accountCodeId: selectedOption, debitAccount: selectedOption }) : setEditedItem({ ...editedItem, accountCodeId: selectedOption, creditAccount: selectedOption });
        }
        else if (field === 'accountCodeName') {

            setEditedItem({ ...editedItem, accountCodeName: selectedOption })
        }
        else {
            setEditedItem({ ...editedItem, accountCaseId: selectedOption })

        }
    };
    const handleDoneClick = async (type, itemId) => {



        setLoading(true);
        try {
            const response = await ClientDocumentCountService.removeDocumentByTypeAndId(client.caseId, client.accountantId, type, itemId, token);

            if (response.ok) {
                await fetchDocumentsNotification(client, token)
                toast.success(t('DocumentProcessed'), {
                    position: direction === 'rtl' ? 'top-left' : 'top-right'
                });
            } else {
                const errorText = await response.text();
                toast.error(t('DocumentNotProcessed', { errorText }));
            }
        } catch (error) {
            toast.error(t('DocumentNotProcessed', { errorText: error.message }));
        }
        setLoading(false);
    };

    const handleNotDoneClick = async (type, itemId) => {
        setLoading(true);
        try {
            const response = await ClientDocumentCountService.addDocumentByTypeAndId(client.caseId, client.accountantId, type, itemId, token)
            if (response.ok) {
                await fetchDocumentsNotification(client, token)
                toast.success(t('DocumentProcessed'), {
                    position: direction === 'rtl' ? 'top-left' : 'top-right'
                });
            } else {
                const errorText = await response.text();
                toast.error(t('DocumentNotProcessed', { errorText }));
            }
        } catch (error) {
            toast.error(t('DocumentNotProcessed', { errorText: error.message }));
        }
        setLoading(false);
    };

    const handleUpdate = async (itemId) => {
        setLoading(true);
        const documentUrl = editedItem.documentUrl;
        const savedId = editedItem.id;
        const savedEntryType = vatHtmlNavigationList[currentVatEditIndex]?.type;

        try {
            if (editedItem.status === 'done') {
                const isValid = await validateDocumentBeforeProcessing(editedItem, t, client);
                if (!isValid) {
                    setLoading(false);
                    return;
                }
            }
            delete editedItem.documentUrl;

            const response = await DocumentService.UpdateDocument(editedItem.documentType, token, editedItem);
            if (editedItem.status == 'done') {
                await handleDoneClick(editedItem.documentType, editedItem.id);
            }
            else if (editedItem.status == 'notDone') {
                await handleNotDoneClick(editedItem.documentType, editedItem.id)
            }

            if (editedAddAccountCard) {
                await handleAccountCardSave(editedItem.accountCodeId, editedItem.accountCodeName, editedItem.accountCaseId);
            }

            if (response.ok) {
                // Keep EditModal open so next/prev navigation still works
                setEditedItem((prev) => ({ ...prev, documentUrl }));
                setEditedAddAccountCard(false);
                setLoading(false);
                toast.success(t("documentUpdateSuccess"), { position: direction === 'rtl' ? 'top-left' : 'top-right' });

                // Background refresh -- no loading spinner, user can continue working
                const refreshTasks = [fetchEnums(user, token, client, true)];
                if (isHtmlModalOpen) {
                    refreshTasks.push(
                        openHtmlViewer(vatTypeNumber, { preserveEditId: savedId, preserveEditType: savedEntryType })
                    );
                }
                Promise.all(refreshTasks).catch(err => console.error('Background refresh error:', err));

                refreshCode1Adjustments(
                    vatReport.clientCaseId,
                    vatReport.StartDate,
                    vatReport.EndDate,
                    Number(vatReport.IncomesNoVats) || 0
                );
            } else {
                setEditedItem((prev) => ({ ...prev, documentUrl }));
                toast.error(t("documentUpdateError"));
                setEditedAddAccountCard(false);
                setLoading(false);
            }
        } catch (error) {
            console.error(error);
            toast.error(t("documentUpdateError"));
            setEditedAddAccountCard(false);
            setLoading(false);
        }
    };


    const handleDeleteClick = (itemId, type) => {
        setItemIdToDelete({ itemId, type });
        setIsConfirmationVisible(true);
    };
    const handleEditDeleteClick = (itemId) => {
        handleDeleteClick(itemId, editedItem.documentType);
        setActionType('delete')
        setEditedItem({});
    };

    const handleReopenEdit = async (item, index) => {
        handleOpenEditSingleEntry(item);
    };


    const handleAccountCardSave = async (AccountCodeId, AccountCodeName, CompanyId) => {

        let response2;
        if (editedItem.documentType === 'Incomes') {

            response2 = await EnumsService.AddAccountCard(client.caseId, AccountCodeId, AccountCodeName, CompanyId, "לקוחות", token);

        } else {

            response2 = await EnumsService.AddAccountCard(client.caseId, AccountCodeId, AccountCodeName, CompanyId, "ספקים", token);
        }
        if (!response2.ok) {

            throw new Error(`HTTP error! status: ${response2.status}`);
        }
    };

    const handleConfirmDelete = async () => {
        // Perform delete operation
        setLoading(true);
        setIsConfirmationVisible(false);
        try {
            const response = await DocumentService.DeleteDocument(itemIdToDelete.type, itemIdToDelete.itemId, token);
            if (response.ok) {
                handleCancel();
                if (isHtmlModalOpen) {
                    await openHtmlViewer(vatTypeNumber);
                }
                RefreshPage()
                // Show success toast
                toast.success(t("documentDeleteSuccess"), {
                    position: direction === 'rtl' ? 'top-left' : 'top-right'
                });
            } else {
                // Handle any errors or display an error toast

                const errorResponse2 = await response.text();


                if (errorResponse2 === 'Income is user-generated and cannot be deleted') {
                    toast.error(t("Income is user-generated and cannot be deleted"));
                } else {
                    toast.error(t("documentDeleteError"));
                }

            }
        } catch (error) {
            console.error('Error deleting document:', error);

            // Show error toast
            toast.error(t("documentDeleteError"));
        }
        setLoading(false);
    };

    const viewDocument = async (docId, type) => {
        const typeStr = String(type);
        let documentType = typeStr;
        let statusChecked = false;
        const docIdStr = String(docId);

        if (typeStr === Constants.EntryType.Expense?.toString()) {
            documentType = 'Expenses';
            statusChecked = client.documentIds?.expenses?.includes(docIdStr) || client.documentIds?.expenses?.includes(docId);
        } else if (typeStr === Constants.EntryType.JournalEntry?.toString()) {
            documentType = 'JournalEntries';
            statusChecked = client.documentIds?.journalEntries?.includes(docIdStr) || client.documentIds?.journalEntries?.includes(docId);
        } else if (typeStr === Constants.EntryType.Income?.toString()) {
            documentType = 'Incomes';
            statusChecked = client.documentIds?.incomes?.includes(docIdStr) || client.documentIds?.incomes?.includes(docId);
        }

        setStatusCheck(!statusChecked);

        try {
            const caseId = vatReport.clientCaseId || client?.caseId || item?.clientCaseId;
            const response = await DocumentService.GetDocumentById(documentType, caseId, docId, token);

            if (!response.ok) {
                throw new Error(`Failed to fetch document: ${response.status}`);
            }

            const documentData = await response.json();
            documentData.documentType = documentType;
            // Document transfer and the shared validation helpers read `docType`.
            documentData.docType = documentType;
            documentData.status = statusChecked ? 'notDone' : 'done';

            handleOpenEditSingleEntry(documentData);
        } catch (error) {
            console.error("Error fetching document:", error);
            console.error("errorFetchingData");
        }
    };

    const RefreshPage = async () => {
        handleCancel();
        await Promise.all([
            fetchDataOnDateRangeChange(vatReport.StartDate, vatReport.EndDate),
            fetchEnums(user, token, client)
        ]);
    };


    const handleAssetClicked = (asset, item) => {
        setItemToAddToAsset(item.id);
        setItemForAssets(item)
        setClickedAsset(asset);
        setShowChooseAsset(true);
    }
    const getReportingMonthDates = () => {
        const sourceDates = isBiMonthly ? [biStartDate, biEndDate] : [date];
        const seen = new Set();
        const result = [];

        sourceDates.forEach((value) => {
            if (!value) return;
            const parsed = value instanceof Date ? value : new Date(value);
            if (Number.isNaN(parsed.getTime())) return;
            // These dates are already UTC day 10 (same values Client Details sends when closing a month).
            const key = `${parsed.getUTCFullYear()}-${parsed.getUTCMonth()}`;
            if (seen.has(key)) return;
            seen.add(key);
            result.push(parsed);
        });

        return result;
    };

    const setCloseReportingMonthChecked = (checked) => {
        closeReportingMonthRef.current = checked;
        setCloseReportingMonth(checked);
    };

    const refreshReportingMonthsOpen = async () => {
        const months = getReportingMonthDates();
        const caseId = client?.caseId || vatReport.clientCaseId;
        if (!caseId || !token || months.length === 0) {
            setReportingMonthsOpen(true);
            return;
        }

        try {
            const openFlags = await Promise.all(months.map(async (monthDate) => {
                const response = await ClientService.CheckIfMonthClosed(caseId, monthDate, token);
                if (!response.ok) return true;
                const isClosed = await response.json();
                return !isClosed;
            }));
            const anyOpen = openFlags.some(Boolean);
            setReportingMonthsOpen(anyOpen);
            if (!anyOpen) {
                setCloseReportingMonthChecked(false);
            }
        } catch (error) {
            console.error('Error checking reporting month status:', error);
            setReportingMonthsOpen(true);
        }
    };

    const closeReportingMonthsIfRequested = async (caseId) => {
        if (!closeReportingMonthRef.current) return;
        if (reportingWay !== 'manual' && reportingWay !== 'automatic') return;

        const months = getReportingMonthDates();
        const targetCaseId = caseId || client?.caseId;
        if (!targetCaseId || months.length === 0) return;

        try {
            for (const monthDate of months) {
                const response = await ClientService.UpdateReportingMonth(targetCaseId, monthDate, true, token);
                if (!response.ok) {
                    const errorText = await response.text();
                    toast.error(`${t('MonthReportNotClosed')}: ${errorText}`);
                    return;
                }
            }
            toast.success(t('MonthReportClosedSuccess'));
        } catch (error) {
            console.error('Error closing reporting month:', error);
            toast.error(t('MonthReportNotClosed'));
        }
    };

    const handleReportingWayChange = async (e) => {
        const selected = e.target.value;

        // Only run check for manual/automatic
        if (selected === 'manual' || selected === 'automatic') {
            const isReady = await canDownloadVatReport();
            if (!isReady) return;
        } else {
            setCloseReportingMonthChecked(false);
        }

        setReportingWay(selected);
    };

    useEffect(() => {
        if (!open) return;
        // Reset only when the modal is opened again. Automatic reporting closes the modal
        // while Tax Authority auth is still in progress, and the choice has to survive that.
        setCloseReportingMonthChecked(false);
    }, [open]);

    useEffect(() => {
        if (!open) return;
        if (reportingWay !== 'manual' && reportingWay !== 'automatic') return;
        refreshReportingMonthsOpen();
    }, [open, reportingWay, isBiMonthly, date, biStartDate, biEndDate, client?.caseId, token]);

    // ======= RENDER =======
    return (
        <Modal open={open} onClose={onClose}>
            <>
                <Box sx={modalStyle}>
                    <div style={{ direction, display: 'flex', flexDirection: 'column', height: '100%' }}>

                        {/* Alerts */}
                        <VatModalAlerts
                            t={t}
                            direction={direction}
                            hasVatControlAccountEntries={hasVatControlAccountEntries}
                            code1AdjustmentAmount={code1AdjustmentAmount}
                            referenceContinuityIssues={referenceContinuityIssues}
                            showReferenceAlert={showReferenceAlert}
                            setShowReferenceAlert={setShowReferenceAlert}
                            viewDocumentDetails={viewDocumentDetails}
                            beautifyNumber={beautifyNumber}
                        />

                        {/* Header: title + date pickers + close */}
                        <VatModalHeader
                            t={t}
                            direction={direction}
                            isRange={isRange}
                            isBiMonthly={isBiMonthly}
                            date={date}
                            startDate={startDate}
                            endDate={endDate}
                            biStartDate={biStartDate}
                            biEndDate={biEndDate}
                            showBiMonth={showBiMonth}
                            setShowBiMonth={setShowBiMonth}
                            handleDateChangeNew={handleDateChangeNew}
                            handleStartDateChangeNew={handleStartDateChangeNew}
                            handleEndDateChangeNew={handleEndDateChangeNew}
                            handleBiMonthDate={handleBiMonthDate}
                            handleRangeChange={handleRangeChange}
                            handleDownloadComparativeVat={handleOpenComparativeVat}
                            onClose={onClose}
                        />

                        {/* Main content: VAT report image + accumulated panel */}
                        <div className="vat-modal-content">
                            <div className="vat-modal-content__report-column">
                                <div className="vat-modal-content__report-wrapper" style={{ height: `${584 * 0.68 + 120}px` }}>
                                    <div ref={vatContentRef} dangerouslySetInnerHTML={{ __html: vatHtmlContent }} />
                                </div>
                                {vatUnprocessedCounts.total > 0 && (
                                    <div className="vat-unprocessed-alert" role="alert" dir={direction}>
                                        <div className="vat-unprocessed-alert__title">
                                            {onlyProcessedDocuments
                                                ? t('UnprocessedNotIncludedInVatTitle')
                                                : t('UnprocessedIncludedInVatTitle')}
                                        </div>
                                        <div>
                                            {onlyProcessedDocuments
                                                ? t('UnprocessedNotIncludedInVatMessage', { count: vatUnprocessedCounts.total })
                                                : t('UnprocessedIncludedInVatMessage', { count: vatUnprocessedCounts.total })}
                                        </div>
                                        {(() => {
                                            const parts = [
                                                vatUnprocessedCounts.income > 0 && `${t('PreviewIncomeVAT')}: ${vatUnprocessedCounts.income}`,
                                                vatUnprocessedCounts.expense > 0 && `${t('PreviewExpenseVAT')}: ${vatUnprocessedCounts.expense}`,
                                                vatUnprocessedCounts.asset > 0 && `${t('PreviewAssetVAT')}: ${vatUnprocessedCounts.asset}`,
                                                vatUnprocessedCounts.exempt > 0 && `${t('ExemptTransactionsDetails')}: ${vatUnprocessedCounts.exempt}`,
                                            ].filter(Boolean);
                                            return parts.length > 0 ? (
                                                <div className="vat-unprocessed-alert__breakdown">{parts.join(' • ')}</div>
                                            ) : null;
                                        })()}
                                    </div>
                                )}
                            </div>
                            <AccumulatedVatPanel t={t} accumulatedVat={accumulatedVat} />
                        </div>

                        {/* Action buttons + reporting options */}
                        <VatModalActions
                            t={t}
                            direction={direction}
                            isRange={isRange}
                            reportingWay={reportingWay}
                            handleReportingWayChange={handleReportingWayChange}
                            closeReportingMonth={closeReportingMonth}
                            setCloseReportingMonth={setCloseReportingMonthChecked}
                            reportingMonthsOpen={reportingMonthsOpen}
                            isBiMonthly={isBiMonthly}
                            numberOfPayments={numberOfPayments}
                            setNumberOfPayments={setNumberOfPayments}
                            paymentMethod={paymentMethod}
                            setPaymentMethod={setPaymentMethod}
                            handleDownloadPdf={handleDownloadPdf}
                            vatContentRef={vatContentRef}
                            setShowPCNModal={setShowPCNModal}
                            handleSaveVatReport={handleSaveVatReport}
                            onClose={onClose}
                            client={client}
                        />
                    </div>
                </Box>

                <HtmlViewerModal
                    open={isHtmlModalOpen}
                    onClose={() => {
                        setIsHtmlModalOpen(false);
                        setVatHtmlNavigationList([]);
                        setCurrentVatEditIndex(-1);
                    }}
                    htmlContent={vatHtml}
                    title={vatHtmlTitle}
                    onRowClick={handleVatRowClick}
                    vatRequestDto={{
                        caseId: vatReport.clientCaseId,
                        startDate: vatReport.StartDate,
                        endDate: vatReport.EndDate,
                        vatType: vatTypeNumber
                    }}
                    token={token}
                />

                <YearlyVatReportModal
                    open={showYearlyVatReport}
                    onClose={() => setShowYearlyVatReport(false)}
                    caseId={vatReport.clientCaseId}
                    client={client}
                    token={token}
                    date={vatReport.EndDate ?? new Date()}
                    onDataUpdate={onDataUpdate}
                />

                <CustomDeleteConfirmationModal
                    isVisible={isConfirmationVisible}
                    onClose={() => setIsConfirmationVisible(false)}
                    onDelete={handleConfirm}
                    confirmMessage={getConfirmMessage()}
                    titleMessage={getTitleMessage()}
                    type={actionType}
                />

                {(loading || isMainLoading) && (
                    <div className="loader-container">
                        <PuffLoader size={100} loading={loading} />
                    </div>
                )}

                {editedItem && Object.keys(editedItem).length > 0 && (
                    <EditModal
                        editedItem={editedItem}
                        fade={false}
                        subAccountantOptions={subAccountantOptions}
                        handlePrevious={handleVatEditPrevious}
                        handleNext={handleVatEditNext}
                        currentIndex={currentVatEditIndex}
                        filteredAndSortedData={vatHtmlNavigationList}
                        NoDocumentsPdf={NoDocumentsPdf}
                        t={t}
                        handleAccountChange={handleAccountChange}
                        isRange={false}
                        isClosedReportingMonth={editedItem.isClosedReportingMonth}
                        accountCardOptions={accountCardOptions}
                        handleChange={handleChange}
                        handleDateChange={handleDateChange}
                        CustomInput2={CustomInput2}
                        setReportingMonthDate={setReportingMonthDate}
                        handleVatRadioChange={handleVatRadioChange}
                        statusCheck={statusCheck}
                        handleRadioChange={handleRadioChange}
                        type={editedItem.documentType}
                        editedAddAccountCard={editedAddAccountCard}
                        handleCloseEditAddNewAccountCardDocument={handleCloseEditAddNewAccountCardDocument}
                        handleEditAddAccountChange={handleEditAddAccountChange}
                        sortCodeIdOptions={sortCodeIdOptions}
                        debitAccountOptions={debitAccountOptions}
                        creditAccountOptions={creditAccountOptions}
                        handleUpdate={handleUpdate}
                        handleCancel={handleCancel}
                        handleDeleteClick={handleEditDeleteClick}
                        client={client}
                        token={token}
                        setAccountCardOptions={setAccountCardOptions}
                        setLoading={setLoading}
                        setEditedAddAccountCard={setEditedAddAccountCard}
                        setEditedItem={setEditedItem}
                        handleReopenEdit={handleReopenEdit}
                        palCodes={formattedPalCodeOptions}
                        RefreshPage={RefreshPage}
                        handleAccountCardSave={handleAccountCardSave}
                        fetchEnums={fetchEnums}
                        assets={assets}
                        assetClicked={handleAssetClicked}
                    />
                )}
            </>
        </Modal>
    );
}
